import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, RotateCw, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const CASES = {
  approved: {
    icon: <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" />,
    title: "¡PAGO APROBADO!",
    color: "border-emerald-500/40",
    desc: "Tu dinero queda retenido por HitTCG hasta que confirmes la recepción de cada carta. El vendedor tiene 3 días para enviar o coordinar el encuentro.",
    primary: { to: "/buyer", label: "Ver mis pedidos", testid: "pay-result-dashboard" },
    secondary: { to: "/catalog", label: "Seguir comprando", testid: "pay-result-catalog" },
  },
  rejected: {
    icon: <XCircle size={56} className="text-[#EF4444] mx-auto mb-4" />,
    title: "EL PAGO NO SE COMPLETÓ",
    color: "border-[#EF4444]/40",
    desc: "Payphone rechazó la transacción. No se te ha cobrado y no se ha creado ninguna orden pagada. Puedes reintentar el pago o cambiar de método.",
    primary: { to: "/cart", label: "Reintentar pago", testid: "pay-result-retry", icon: <RotateCw size={16} className="mr-2" /> },
    secondary: { to: "/buyer", label: "Ir a mis pedidos", testid: "pay-result-dashboard" },
  },
  conflict: {
    icon: <AlertTriangle size={56} className="text-amber-400 mx-auto mb-4" />,
    title: "LA CARTA YA NO ESTABA DISPONIBLE",
    color: "border-amber-500/40",
    desc: "Recibimos tu pago, pero otra persona la había reservado antes. HitTCG te reembolsará el total y te avisaremos cuando esté hecho.",
    primary: { to: "/buyer", label: "Ir a mis pedidos", testid: "pay-result-dashboard-conflict", icon: <ShoppingCart size={16} className="mr-2" /> },
    secondary: { to: "/catalog", label: "Seguir buscando", testid: "pay-result-catalog" },
  },
  unavailable: {
    icon: <AlertTriangle size={56} className="text-amber-400 mx-auto mb-4" />,
    title: "LA CARTA YA NO ESTABA DISPONIBLE",
    color: "border-amber-500/40",
    desc: "Otra persona la compró antes. No confirmamos tu pago: Payphone lo anula automáticamente y no se te cobra.",
    primary: { to: "/catalog", label: "Seguir buscando", testid: "pay-result-catalog-unavailable", icon: <ShoppingCart size={16} className="mr-2" /> },
    secondary: { to: "/buyer", label: "Ir a mis pedidos", testid: "pay-result-dashboard" },
  },
  cancelled: {
    icon: <AlertTriangle size={56} className="text-amber-400 mx-auto mb-4" />,
    title: "PAGO CANCELADO",
    color: "border-amber-500/40",
    desc: "Cancelaste el pago en Payphone. Tu carrito sigue disponible por si quieres retomarlo cuando estés listo.",
    primary: { to: "/cart", label: "Volver al carrito", testid: "pay-result-cart", icon: <ShoppingCart size={16} className="mr-2" /> },
    secondary: { to: "/catalog", label: "Seguir comprando", testid: "pay-result-catalog" },
  },
  error: {
    icon: <AlertTriangle size={56} className="text-amber-400 mx-auto mb-4" />,
    title: "NO PUDIMOS CONFIRMAR TU PAGO",
    color: "border-amber-500/40",
    desc: "Hubo un problema al validar el resultado con Payphone. Si el cobro se completó en tu banco, la orden aparecerá como PAGADA en un par de minutos. Si no, puedes reintentar.",
    primary: { to: "/buyer", label: "Ver mis pedidos", testid: "pay-result-dashboard" },
    secondary: { to: "/cart", label: "Reintentar pago", testid: "pay-result-retry", icon: <RotateCw size={16} className="mr-2" /> },
  },
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const tx = params.get("tx") || "";
  const outcome = params.get("outcome") || "approved";
  const reason = params.get("reason") || "";
  const c = CASES[outcome] || CASES.approved;
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    // Best-effort: find the buyer's most recent order tied to this bundle/tx to link deep
    if (!tx || outcome !== "approved") return;
    api.get(`/orders/mine?limit=5`).then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data?.items || [];
      const match = arr.find((o) => o.bundle_id === tx || o.id === tx);
      if (match) setOrderId(match.id);
    }).catch(() => {});
  }, [tx, outcome]);

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid={`pay-result-${outcome}`}>
      <div className={`bg-[#111] border rounded-md p-8 ${c.color}`}>
        {c.icon}
        <h1 className="font-display text-3xl mb-2">{c.title}</h1>
        <p className="text-neutral-400 text-sm mb-6">{c.desc}</p>
        {reason && <p className="text-neutral-600 text-xs mb-4">Ref: {reason}</p>}
        <div className="flex gap-2 flex-col">
          <Link to={orderId && outcome === "approved" ? `/order/${orderId}` : c.primary.to}>
            <Button data-testid={c.primary.testid} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
              {c.primary.icon}{orderId && outcome === "approved" ? "Ver mi pedido" : c.primary.label}
            </Button>
          </Link>
          <Link to={c.secondary.to}>
            <Button data-testid={c.secondary.testid} variant="ghost" className="w-full text-neutral-400">
              {c.secondary.icon}{c.secondary.label}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
